/**
 * Custom hook for Factory action handlers
 * Extracted from App.js to reduce complexity
 * Uses state from useFactoryState hook
 */
import { useState, useCallback } from "react";
import api from "../lib/axios";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export function useFactoryActions(factoryState, { fetchQuoteTypes, fetchAllSalesData }) {
  const {
    factoryPassword,
    factoryParsedItems,
    setFactoryParsedItems,
    factoryProjectName,
    setFactoryProjectName,
    factoryInvoiceNo,
    setFactoryInvoiceNo,
    factoryBulkMaterial,
    factoryBulkColor,
    factoryBulkFactory,
    factoryAdjustPercent,
    setFactoryAdjustPercent,
    factoryAdjustApplied,
    setFactoryAdjustApplied,
    isFactoryParsedItemsOpen,
    setIsFactoryParsedItemsOpen,
    setFactoryAllImages,
    factoryManualItem,
    setFactoryManualItem,
    setIsFactoryManualAddOpen,
    factoryEditType,
    setFactoryEditType,
    setIsFactoryEditOpen,
    factoryDeleteType,
    setFactoryDeleteType,
    setIsFactoryDeleteOpen,
    factoryDuplicateType,
    setFactoryDuplicateType,
    factoryDuplicateName,
    setFactoryDuplicateName,
    setIsFactoryDuplicateOpen,
    setIsFactoryUploading,
    setFactoryParsedFilename,
    loadAllProjects,
    loadAllTypeNames,
    fetchFactoryData
  } = factoryState;

  // State for merge confirmation dialog
  const [mergeConfirmation, setMergeConfirmation] = useState(null);

  // Auto-match items with existing project data
  const autoMatchProjectItems = useCallback(async (projectName) => {
    if (!projectName || factoryParsedItems.length === 0) return;
    
    try {
      const response = await api.get(`${API}/project-items/${encodeURIComponent(projectName)}`);
      const existingItems = response.data.items || [];
      
      if (existingItems.length === 0) {
        toast.info(`No se encontraron elementos previos del proyecto "${projectName}"`);
        return;
      }

      // Create lookup by code (uppercase, trimmed)
      const itemsByCode = {};
      for (const item of existingItems) {
        const code = (item.code || '').toUpperCase().trim();
        if (code) {
          itemsByCode[code] = item;
        }
      }

      // Update parsed items with matching data
      let matchCount = 0;
      let dimensionMismatchCount = 0;
      
      setFactoryParsedItems(prev => prev.map(parsedItem => {
        const code = (parsedItem.item_code || '').toUpperCase().trim();
        const match = itemsByCode[code];
        
        if (match) {
          matchCount++;
          
          // Check dimension mismatch
          const widthMatch = !parsedItem.width_m || !match.width_m || 
            Math.abs(parsedItem.width_m - match.width_m) < 0.05;
          const heightMatch = !parsedItem.height_m || !match.height_m || 
            Math.abs(parsedItem.height_m - match.height_m) < 0.05;
          
          if (!widthMatch || !heightMatch) {
            dimensionMismatchCount++;
          }
          
          return {
            ...parsedItem,
            image_data: parsedItem.image_data || match.image_data,
            image_hash: parsedItem.image_hash || match.image_hash,
            _dimension_mismatch: !widthMatch || !heightMatch,
            _match_width: match.width_m,
            _match_height: match.height_m,
            material: parsedItem.material || match.material,
          };
        }
        return parsedItem;
      }));

      if (matchCount > 0) {
        let message = `✅ ${matchCount} elementos coinciden con datos previos del proyecto`;
        if (dimensionMismatchCount > 0) {
          message += ` (⚠️ ${dimensionMismatchCount} con diferencias en dimensiones)`;
        }
        toast.success(message, { duration: 5000 });
      }
      
    } catch (error) {
      console.error('Error in auto-match project items:', error);
      // Silent fail - non-critical feature
    }
  }, [factoryParsedItems, setFactoryParsedItems]);

  // Handle project name change and trigger auto-match
  const handleProjectNameChange = useCallback((newProjectName) => {
    setFactoryProjectName(newProjectName);
    if (newProjectName && newProjectName.length > 2) {
      setTimeout(() => {
        autoMatchProjectItems(newProjectName);
      }, 500);
    }
  }, [setFactoryProjectName, autoMatchProjectItems]);

  // Apply factory name to all items
  const applyFactoryBulkFactory = useCallback(() => {
    if (!factoryBulkFactory) return;
    setFactoryParsedItems(prev => prev.map(item => ({
      ...item,
      factory_name: factoryBulkFactory
    })));
    toast.success(`Fábrica "${factoryBulkFactory}" aplicada a todos`);
  }, [factoryBulkFactory, setFactoryParsedItems]);

  // Handle factory manual add image
  const handleFactoryManualImage = useCallback((file) => {
    if (!file || !file.type.startsWith('image/')) {
      toast.error('Por favor selecciona una imagen válida');
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      setFactoryManualItem(prev => ({ ...prev, image_data: e.target.result }));
    };
    reader.readAsDataURL(file);
  }, [setFactoryManualItem]);

  // Handle factory manual drag/drop
  const handleFactoryManualDrop = useCallback((e, setDragActive) => {
    e.preventDefault();
    setDragActive(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFactoryManualImage(file);
  }, [handleFactoryManualImage]);

  // Handle factory manual paste
  const handleFactoryManualPaste = useCallback((e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (let item of items) {
      if (item.type.startsWith('image/')) {
        const file = item.getAsFile();
        if (file) handleFactoryManualImage(file);
        break;
      }
    }
  }, [handleFactoryManualImage]);

  // Save factory manual type
  const handleSaveFactoryManualType = useCallback(async () => {
    const { name, material, color, factory_name, project_name, width, height, cost, image_data, extra_specs } = factoryManualItem;
    
    if (!name || !width || !height || !cost || !project_name) {
      toast.error('Por favor completa los campos requeridos: Nombre, Ancho, Alto, Costo y Proyecto');
      return;
    }

    const widthNum = parseFloat(width);
    const heightNum = parseFloat(height);
    const costNum = parseFloat(cost);
    const area = widthNum * heightNum;
    const costPerM2 = costNum / area;

    try {
      const itemToSave = {
        item_code: name,
        custom_name: name,
        material: material || "Aluminio",
        color: color || null,
        factory_name: factory_name || null,
        project_name: project_name,
        width_m: widthNum,
        height_m: heightNum,
        area_m2: area,
        cost_usd_set: costNum,
        cost_usd_m2: costPerM2,
        image_data: image_data,
        opening_design: name,
        extra_specs: extra_specs || null
      };

      await api.post(
        `${API}/factory/save-items`,
        { 
          items: [itemToSave], 
          password: factoryPassword,
          project_name: project_name
        }
      );

      toast.success(`Tipo "${name}" guardado exitosamente`);
      setIsFactoryManualAddOpen(false);
      setFactoryManualItem({
        name: "",
        material: "Aluminio",
        color: "",
        factory_name: "",
        project_name: "",
        width: "",
        height: "",
        cost: "",
        image_data: null,
        extra_specs: ""
      });
      fetchFactoryData();
    } catch (error) {
      const message = error.response?.data?.detail || 'Error al guardar tipo';
      toast.error(message);
    }
  }, [factoryManualItem, factoryPassword, setIsFactoryManualAddOpen, setFactoryManualItem, fetchFactoryData]);

  // SEC-002 FIX: Helper to get auth headers with factory password
  const getFactoryAuthHeaders = useCallback(() => {
    return {
      'X-Factory-Password': factoryPassword
    };
  }, [factoryPassword]);

  // Update factory type
  const handleUpdateFactoryType = useCallback(async (confirmMerge = false) => {
    if (!factoryEditType) return;
    const confirm = confirmMerge === true;
    
    try {
      // SEC-002 FIX: Send password via header instead of query param
      const response = await api.put(
        `${API}/factory/types/${factoryEditType.id}`,
        {
          name: factoryEditType.name,
          material: factoryEditType.material,
          color: factoryEditType.color,
          factory_name: factoryEditType.factory_name,
          series: factoryEditType.series,
          glass_spec: factoryEditType.glass_spec,
          lock_type: factoryEditType.lock_type,
          extra_specs: factoryEditType.extra_specs,
          image_data: factoryEditType.image_data,
          confirm_merge: confirm
        },
        { headers: getFactoryAuthHeaders() }
      );
      
      if (response.data?.requires_confirmation) {
        setMergeConfirmation(response.data);
        return;
      }
      
      if (response.data?.merged_into) {
        toast.success(`Fusionado: ${response.data.new_entries_count} muestras, promedio $${response.data.new_average}/m²`);
      } else {
        toast.success('Tipo actualizado exitosamente');
      }
      setIsFactoryEditOpen(false);
      setFactoryEditType(null);
      setMergeConfirmation(null);
      fetchFactoryData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar tipo');
    }
  }, [factoryEditType, getFactoryAuthHeaders, setIsFactoryEditOpen, setFactoryEditType, fetchFactoryData]);
  
  // Confirm merge operation
  const handleConfirmMerge = useCallback(async () => {
    await handleUpdateFactoryType(true);
  }, [handleUpdateFactoryType]);
  
  // Cancel merge operation
  const handleCancelMerge = useCallback(() => {
    setMergeConfirmation(null);
  }, []);

  // Delete factory type
  const handleDeleteFactoryType = useCallback(async () => {
    if (!factoryDeleteType) return;
    
    try {
      // SEC-002 FIX: Send password via header
      await api.delete(`${API}/factory/types/${factoryDeleteType.id}`, {
        headers: getFactoryAuthHeaders()
      });
      toast.success('Tipo eliminado exitosamente');
      setIsFactoryDeleteOpen(false);
      setFactoryDeleteType(null);
      fetchFactoryData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar tipo');
    }
  }, [factoryDeleteType, getFactoryAuthHeaders, setIsFactoryDeleteOpen, setFactoryDeleteType, fetchFactoryData]);

  // Duplicate factory type
  const handleDuplicateFactoryType = useCallback(async () => {
    if (!factoryDuplicateType) return;
    
    try {
      // SEC-002 FIX: Send password via header
      await api.post(
        `${API}/factory/types/${factoryDuplicateType.id}/duplicate?new_name=${encodeURIComponent(factoryDuplicateName)}`,
        {},
        { headers: getFactoryAuthHeaders() }
      );
      toast.success('Tipo duplicado exitosamente');
      setIsFactoryDuplicateOpen(false);
      setFactoryDuplicateType(null);
      setFactoryDuplicateName("");
      fetchFactoryData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al duplicar tipo');
    }
  }, [factoryDuplicateType, getFactoryAuthHeaders, factoryDuplicateName, setIsFactoryDuplicateOpen, setFactoryDuplicateType, setFactoryDuplicateName, fetchFactoryData]);

  // Handle factory file upload
  const handleFactoryFileUpload = useCallback(async (file) => {
    setIsFactoryUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('password', factoryPassword); // Password in form data is OK for POST

    try {
      await Promise.all([
        loadAllProjects(),
        fetchQuoteTypes(),
        loadAllTypeNames()
      ]);
      
      // SEC-002 FIX: Also send password in header for parse-file endpoint
      const response = await api.post(`${API}/factory/parse-file`, formData, {
        headers: { 
          'Content-Type': 'multipart/form-data',
          'X-Factory-Password': factoryPassword
        }
      });
      
      if (response.data.items.length === 0) {
        toast.error('No se encontraron elementos en el archivo');
      } else {
        toast.success(`Se encontraron ${response.data.items.length} elementos`);
        setFactoryParsedFilename(response.data.filename);
        setFactoryProjectName(response.data.project_name || "");
        setFactoryInvoiceNo(response.data.invoice_no || "");
        setFactoryAdjustPercent("");
        setFactoryAdjustApplied(false);
        setFactoryParsedItems(response.data.items.map(item => ({
          ...item,
          material: item.material || "Aluminio",
          custom_name: item.opening_design || item.item_code,
          project_name: response.data.project_name || item.project_name || ""
        })));
        const allImgs = response.data.items
          .filter(item => item.image_data)
          .map(item => item.image_data);
        setFactoryAllImages([...new Set(allImgs)]);
        setIsFactoryParsedItemsOpen(true);
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Error al parsear archivo';
      toast.error(message);
    } finally {
      setIsFactoryUploading(false);
    }
  }, [factoryPassword, loadAllProjects, fetchQuoteTypes, loadAllTypeNames, setIsFactoryUploading, setFactoryParsedFilename, setFactoryProjectName, setFactoryInvoiceNo, setFactoryAdjustPercent, setFactoryAdjustApplied, setFactoryParsedItems, setFactoryAllImages, setIsFactoryParsedItemsOpen]);

  // Update factory parsed item
  const updateFactoryParsedItem = useCallback((itemId, field, value) => {
    setFactoryParsedItems(prev => prev.map(item => 
      item.id === itemId ? { ...item, [field]: value } : item
    ));
  }, [setFactoryParsedItems]);

  // Apply bulk material
  const applyFactoryBulkMaterial = useCallback(() => {
    if (!factoryBulkMaterial) return;
    setFactoryParsedItems(prev => prev.map(item => ({
      ...item,
      material: factoryBulkMaterial
    })));
    toast.success(`Material "${factoryBulkMaterial}" aplicado a todos`);
  }, [factoryBulkMaterial, setFactoryParsedItems]);

  // Apply bulk color
  const applyFactoryBulkColor = useCallback(() => {
    if (!factoryBulkColor) return;
    setFactoryParsedItems(prev => prev.map(item => ({
      ...item,
      color: factoryBulkColor
    })));
    toast.success(`Color "${factoryBulkColor}" aplicado a todos`);
  }, [factoryBulkColor, setFactoryParsedItems]);

  // Apply percentage adjustment
  const applyFactoryPercentAdjust = useCallback(() => {
    const percent = parseFloat(factoryAdjustPercent);
    if (isNaN(percent)) {
      toast.error('Ingresa un porcentaje válido');
      return;
    }
    
    const multiplier = 1 + (percent / 100);
    
    setFactoryParsedItems(prev => prev.map(item => {
      const newCostM2 = item.cost_usd_m2 ? item.cost_usd_m2 * multiplier : null;
      const newCostSet = item.cost_usd_set ? item.cost_usd_set * multiplier : null;
      return {
        ...item,
        cost_usd_m2: newCostM2 ? Math.round(newCostM2 * 100) / 100 : null,
        cost_usd_set: newCostSet ? Math.round(newCostSet * 100) / 100 : null,
        _adjustment_percent: percent
      };
    }));
    
    setFactoryAdjustApplied(true);
    toast.success(`Ajuste de ${percent > 0 ? '+' : ''}${percent}% aplicado a todos los costos`);
  }, [factoryAdjustPercent, setFactoryParsedItems, setFactoryAdjustApplied]);

  // Save factory parsed items
  const saveFactoryParsedItems = useCallback(async () => {
    if (!factoryProjectName?.trim()) {
      toast.error('El nombre del proyecto es obligatorio');
      return;
    }
    
    const itemsWithoutMaterial = factoryParsedItems.filter(item => !item.material);
    if (itemsWithoutMaterial.length > 0) {
      toast.error(`${itemsWithoutMaterial.length} elementos sin material asignado`);
      return;
    }
    
    try {
      const response = await api.post(`${API}/factory/save-items`, {
        items: factoryParsedItems,
        password: factoryPassword,
        project_name: factoryProjectName.trim(),
        adjustment_percent: factoryAdjustApplied ? parseFloat(factoryAdjustPercent) : null
      });
      
      toast.success(
        `Guardado: ${response.data.new_types} nuevos, ${response.data.updated_types} actualizados`,
        { duration: 5000 }
      );
      
      setIsFactoryParsedItemsOpen(false);
      setFactoryParsedItems([]);
      setFactoryProjectName("");
      setFactoryAdjustPercent("");
      setFactoryAdjustApplied(false);
      fetchFactoryData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    }
  }, [factoryProjectName, factoryParsedItems, factoryPassword, factoryAdjustApplied, factoryAdjustPercent, setIsFactoryParsedItemsOpen, setFactoryParsedItems, setFactoryProjectName, setFactoryAdjustPercent, setFactoryAdjustApplied, fetchFactoryData]);

  return {
    // Merge confirmation state
    mergeConfirmation,
    setMergeConfirmation,
    
    // Action handlers
    autoMatchProjectItems,
    handleProjectNameChange,
    applyFactoryBulkFactory,
    handleFactoryManualImage,
    handleFactoryManualDrop,
    handleFactoryManualPaste,
    handleSaveFactoryManualType,
    handleUpdateFactoryType,
    handleConfirmMerge,
    handleCancelMerge,
    handleDeleteFactoryType,
    handleDuplicateFactoryType,
    handleFactoryFileUpload,
    updateFactoryParsedItem,
    applyFactoryBulkMaterial,
    applyFactoryBulkColor,
    applyFactoryPercentAdjust,
    saveFactoryParsedItems
  };
}

export default useFactoryActions;
